/* eslint-disable no-debugger */
import { FC, useMemo } from 'react';
import ReactFC from 'react-fusioncharts';
import FusionCharts, { ChartObject } from 'fusioncharts';
import TimeSeries from 'fusioncharts/fusioncharts.timeseries';
import strings from '@src/app/strings/LocalisedStrings';
import { DateTimeFormats } from '@src/app/enums';
import { dateFormatter } from '@src/app/utils/GenericUtils';
import { ChartSchema } from '@src/app/types';
import { ValuationChartSeriesData } from 'clientportalshared';

ReactFC.fcRoot(FusionCharts, TimeSeries);

export interface DashboardChartProps {
  charts: DashboardChartData[];
}

export interface DashboardChartData {
  valueOverTime: ValuationChartSeriesData[] | undefined;
  name: string;
  id: number;
}

export const DashboardChart: FC<DashboardChartProps> = ({ charts }): JSX.Element => {
  const chartSchema = useMemo(() => {
    const schema: ChartSchema[] = [{ name: strings.time, type: 'date', format: '%d-%m-%Y' }];

    charts.forEach((chart) => {
      schema.push({
        name: chart.id === 0 ? chart.name : `${chart.name} (${chart.id})`,
        type: 'number',
      });
    });
    return schema;
  }, [charts]);

  const data: (string | number)[][] = useMemo(() => {
    const tempData: (string | number)[][] = [];

    charts.forEach((chart, index) => {
      chart.valueOverTime?.forEach((item) => {
        const date = dateFormatter(item.date!, false, DateTimeFormats.FcDateFormat);
        const existingRow = tempData.find((row) => row[0] === date);

        if (existingRow) {
          existingRow[index + 1] = item.value!;
          return;
        }
        const newRow = new Array(charts.length + 1).fill(null);
        newRow[0] = date;
        newRow[index + 1] = item.value!;
        tempData.push(newRow);
      });
    });

    return tempData;
  }, [charts]);

  const fusionDataTable = new FusionCharts.DataStore().createDataTable(data, chartSchema);

  const chartConfig: ChartObject = {
    type: 'timeseries',
    width: '100%',
    height: '400',
    dataFormat: 'json',
    dataEmptyMessage: strings.emptyPortfolioDataMessage,
    dataSource: {
      chart: {
        theme: 'fusion',
        numberPrefix: '$',
        lineThickness: '2',
        showLegend: 0,
        showTimeNav: 0,
      },
      tooltip: {
        style: {
          container: {
            minWidth: '180px',
            maxWidth: '300px',
          },
        },
      },
      navigator: {
        enabled: 0,
      },
      yaxis: [
        {
          plot: charts.map((chart) => ({
            value: chart.id === 0 ? chart.name : `${chart.name} (${chart.id})`,
            type: 'line',
            title: chart.id === 0 ? chart.name : `${chart.name} (${chart.id})`,
            connectnulldata: true,
          })),
          title: '',
          format: {
            prefix: '$',
          },
        },
      ],
      extensions: {
        standardRangeSelector: {
          enabled: '0',
        },
        customRangeSelector: {
          enabled: '0',
        },
      },
      data: fusionDataTable,
    },
  };

  return <ReactFC {...chartConfig} />;
};
